import React, {useEffect, useState} from 'react';
import {ScheduleData} from "../../../type/ScheduleData";
import {Button, Form, Modal} from 'react-bootstrap';
import {clone} from "../../../util/objectUtil";
import {formatDateForHumans} from "../../../util/dateUtil";
import {loadScheduleTypes} from "../../../util/restClient";
import {BasicComponentProps} from "../../../type/BasicComponentProps";

export type SchedulesGridProps = BasicComponentProps & {
	data: ScheduleData;
	onSaved: (data: ScheduleData) => any;
	onDeleted?: (data: ScheduleData) => any;
	onCancelled: () => any;
};

function ScheduleForm({data, onSaved, onDeleted, onCancelled, eventManager}: SchedulesGridProps) {
	const [scheduleTypes, setScheduleTypes] = useState<string[] | null>(null);
	const [formData, setFormData] = useState<ScheduleData>(clone(data));

	const loadTypes = () => {
		loadScheduleTypes()
			.then(
				setScheduleTypes,
				(err) => eventManager.err(String(err))
			);
	}

	useEffect(loadTypes, []);

	const updateField = (action: () => any) => {
		action();
		setFormData(clone(formData));
	}

	return (
		<Modal show={true} onHide={onCancelled}>
			<Modal.Header closeButton>
				<Modal.Title>Schedule for <strong>{formData.job.name}</strong></Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form onSubmit={() => onSaved(formData)}>
					<Form.Group className="mb-3" controlId="pluginId">
						<Form.Label>ID</Form.Label>
						<Form.Control
							type="text"
							disabled={true}
							value={String(formData.id)}>
						</Form.Control>
					</Form.Group>
					<Form.Group className="mb-3" controlId="pluginId">
						<Form.Label>Type</Form.Label>
						<Form.Select
							aria-label="Default select example"
							value={formData.scheduleType}
							onChange={(e) => updateField(() => formData.scheduleType = e.target.value)}
						>
							{
								scheduleTypes ?
									<>
										{scheduleTypes.map((st) => <option key={st} value={st}>{st}</option>)}
									</> : <option>loading...</option>
							}
						</Form.Select>
					</Form.Group>
					<Form.Group className="mb-3" controlId="active">
						<Form.Switch
							label="Is Active"
							checked={formData.active}
							onChange={(e) => updateField(() => formData.active = !formData.active)}
						/>
					</Form.Group>
					{(formData.scheduleType === 'OneTime' || formData.scheduleType === 'Daily') &&
						<Form.Group className="mb-3" controlId="startTime">
							<Form.Label>{formData.scheduleType === 'Daily' ? 'Time of day' : 'Start'}</Form.Label>
							<Form.Control
								type="datetime-local"
								value={formData.startTime ? formatDateForHumans(formData.startTime) : ""}
								onChange={(e) => updateField(() => formData.startTime = new Date(e.target.value))}
							/>
						</Form.Group>
					}
					{formData.scheduleType === 'Interval' &&
						<Form.Group className="mb-3" controlId="intervalMs">
							<Form.Label>Interval (ms)</Form.Label>
							<Form.Control
								type="text"
								value={formData.intervalMs || ''}
								onChange={(e) => updateField(() => formData.intervalMs = Number(e.target.value))}
							/>
						</Form.Group>
					}
					{formData.scheduleType === 'Cron' &&
						<Form.Group className="mb-3" controlId="cronExpression">
							<Form.Label>Cron expression</Form.Label>
							<Form.Control
								type="text"
								placeholder="sec min hour day month weekday (e.g. 0 0 6 * * *)"
								value={formData.cronExpression || ''}
								onChange={(e) => updateField(() => formData.cronExpression = e.target.value)}
							/>
							<Form.Text muted as="div">
								6 fields: <code>sec min hour day month weekday</code> (first field is seconds!). Examples:
								<ul className="mb-0">
									<li><code>0 0/10 * * * *</code> &mdash; every 10 minutes</li>
									<li><code>0 0 * * * *</code> &mdash; every hour (on the hour)</li>
									<li><code>0 0 6 * * *</code> &mdash; every day at 06:00</li>
									<li><code>0 30 8 * * MON-FRI</code> &mdash; weekdays at 08:30</li>
									<li><code>*/30 * * * * *</code> &mdash; every 30 seconds</li>
								</ul>
							</Form.Text>
						</Form.Group>
					}
				</Form>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onCancelled}>Close</Button>
				<Button variant="danger" disabled={onDeleted === undefined}
						onClick={() => onDeleted === undefined ? null : onDeleted(formData)}>Delete</Button>
				<Button variant="success" onClick={() => onSaved(formData)}>Save</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default ScheduleForm;
